import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  Dumbbell, 
  Flame, 
  Calendar,
  BarChart3,
  TrendingUp 
} from 'lucide-react';

const DashboardStats = ({ loading, stats }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  
  // Calculate days since user registration
  const calculateDaysSince = (dateObj) => {
    if (!dateObj) return 0;
    
    const diffTime = Math.abs(new Date() - dateObj);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };
  
  // Calculate average workouts per week
  const calculateWeeklyAverage = (workoutCount, userSince) => {
    if (!userSince || workoutCount === 0) return 0;
    
    const daysSince = calculateDaysSince(userSince);
    const weeksSince = Math.max(daysSince / 7, 1); // At least 1 week
    return (workoutCount / weeksSince).toFixed(1);
  };
  
  // Simplified progression calculation (placeholder)
  const calculateProgression = (workoutCount, completedSets) => {
    if (workoutCount === 0) return 0;
    // Simple heuristic: progression is ratio of sets to workouts, normalized to percentage
    return Math.min(Math.round((completedSets / workoutCount) * 10), 100);
  };
  
  // Stats to display
  const statItems = [
    {
      id: 'total-workouts',
      name: t('dashboard.stats.workouts'),
      value: stats.workoutCount,
      icon: Dumbbell,
      iconClass: 'text-blue-600 dark:text-blue-400',
      bgClass: 'bg-blue-100 dark:bg-blue-900/30'
    },
    {
      id: 'completed-sets',
      name: t('dashboard.stats.completedSets'),
      value: stats.completedSets,
      icon: Flame,
      iconClass: 'text-green-600 dark:text-green-400',
      bgClass: 'bg-green-100 dark:bg-green-900/30'
    },
    {
      id: 'active-days',
      name: t('standaloneDashboard.stats.activeDays'),
      value: calculateDaysSince(stats.userSince),
      icon: Calendar,
      iconClass: 'text-purple-600 dark:text-purple-400',
      bgClass: 'bg-purple-100 dark:bg-purple-900/30'
    },
    {
      id: 'weekly-average',
      name: t('standaloneDashboard.stats.weeklyAverage'),
      value: calculateWeeklyAverage(stats.workoutCount, stats.userSince),
      suffix: t('standaloneDashboard.stats.perWeek'),
      icon: BarChart3,
      iconClass: 'text-amber-600 dark:text-amber-400',
      bgClass: 'bg-amber-100 dark:bg-amber-900/30'
    },
    {
      id: 'progression',
      name: t('standaloneDashboard.stats.progression'),
      value: calculateProgression(stats.workoutCount, stats.completedSets),
      suffix: '%',
      icon: TrendingUp,
      iconClass: 'text-indigo-600 dark:text-indigo-400',
      bgClass: 'bg-indigo-100 dark:bg-indigo-900/30'
    }
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
      {statItems.map((item) => (
        <StatCard 
          key={item.id}
          loading={loading}
          name={item.name}
          value={item.value}
          suffix={item.suffix}
          icon={item.icon}
          iconClass={item.iconClass}
          bgClass={item.bgClass}
        />
      ))}
    </div>
  );
};

// Individual stat card component
const StatCard = ({ loading, name, value, suffix = '', icon: Icon, iconClass, bgClass }) => {
  const { isDark } = useTheme();
  
  return (
    <div className={`p-4 rounded-xl shadow-sm transition-all ${
      isDark ? 'bg-gray-800 border border-gray-700 hover:border-gray-600' : 'bg-white border border-gray-200 hover:border-gray-300'
    }`}>
      <div className="flex items-center mb-3">
        <div className={`p-2 rounded-lg mr-3 ${bgClass}`}>
          <Icon className={`h-5 w-5 ${iconClass}`} />
        </div>
        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
          {name}
        </h3>
      </div>
      
      {loading ? (
        <div className="h-8 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
      ) : (
        <p className="text-2xl font-bold text-gray-900 dark:text-white">
          {value}{suffix && <span className="text-sm font-medium ml-1 text-gray-500 dark:text-gray-400">{suffix}</span>}
        </p>
      )}
    </div>
  );
};

export default DashboardStats;